/**
 * ApprovalPanel Component
 * =======================
 * Phase 7: Content Approval Workflow
 * 
 * Displays approval status, comments, and actions for designs/events.
 */

import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import { ScrollArea } from '../ui/scroll-area';
import { Separator } from '../ui/separator';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { toast } from 'sonner';
import {
  CheckCircle, XCircle, Clock, Send, MessageSquare, AlertTriangle,
  User, Calendar, RotateCcw, FileText, History, Loader2
} from 'lucide-react';
import { usePermission, FEATURE_PERMISSIONS } from '../common/PermissionButton';
import { useAuth } from '../../contexts/AuthContext';
import { formatDistanceToNow } from 'date-fns';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Approval status badge styles
const STATUS_CONFIG = {
  not_submitted: { label: 'Draft', color: 'bg-slate-500', icon: FileText },
  pending: { label: 'Pending Approval', color: 'bg-yellow-500', icon: Clock },
  approved: { label: 'Approved', color: 'bg-green-500', icon: CheckCircle },
  rejected: { label: 'Rejected', color: 'bg-red-500', icon: XCircle },
  revision_requested: { label: 'Revisions Requested', color: 'bg-orange-500', icon: AlertTriangle }
};

// Status Badge Component
export const ApprovalStatusBadge = ({ status, size = 'default' }) => {
  const config = STATUS_CONFIG[status] || STATUS_CONFIG.not_submitted;
  const Icon = config.icon;
  
  const sizeClasses = size === 'sm' 
    ? 'text-xs px-2 py-0.5' 
    : 'text-sm px-3 py-1';
  
  return (
    <Badge 
      className={`${config.color} text-white ${sizeClasses} flex items-center gap-1`}
      data-testid={`approval-status-${status}`}
    >
      <Icon className={size === 'sm' ? 'h-3 w-3' : 'h-4 w-4'} />
      {config.label}
    </Badge>
  );
};

// Comment Item Component
const CommentItem = ({ comment, currentUserId, onDelete }) => {
  const isOwn = comment.user_id === currentUserId;
  const timeAgo = comment.created_at 
    ? formatDistanceToNow(new Date(comment.created_at), { addSuffix: true })
    : '';

  return (
    <div className={`p-3 rounded-lg ${isOwn ? 'bg-blue-50 dark:bg-blue-900/20 ml-4' : 'bg-slate-100 dark:bg-slate-700/50 mr-4'}`}>
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2">
          <User className="h-3 w-3 text-slate-400" />
          <span className="text-xs font-medium dark:text-white">{comment.user_name || 'Unknown'}</span>
        </div>
        <span className="text-xs text-muted-foreground">{timeAgo}</span>
      </div>
      <p className="text-sm dark:text-slate-300">{comment.content}</p>
      {isOwn && (
        <Button
          variant="ghost"
          size="sm"
          className="text-xs text-red-500 hover:text-red-700 mt-1 h-6 px-2"
          onClick={() => onDelete(comment.id)}
        >
          Delete
        </Button>
      )}
    </div>
  );
};

// Main Approval Panel Component
export const ApprovalPanel = ({
  entityType, // 'design' | 'event'
  entityId,
  entityName,
  approvalStatus,
  rejectionReason,
  revisionNotes,
  submittedAt,
  approvedAt,
  rejectedAt,
  onStatusChange,
  readOnly = false
}) => {
  const { user } = useAuth();
  const canApproveContent = usePermission(FEATURE_PERMISSIONS.APPROVE_CONTENT);
  const canApproveSchedule = usePermission(FEATURE_PERMISSIONS.APPROVE_SCHEDULE);
  
  // Check if user is a manager role
  const isManager = user?.role && ['super_admin', 'admin', 'ceo', 'president', 'director_of_sales', 'sales_manager', 'operations_manager', 'general_manager'].includes(user.role);
  
  const [comments, setComments] = useState([]);
  const [auditLog, setAuditLog] = useState([]);
  const [loading, setLoading] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [showRevisionDialog, setShowRevisionDialog] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [revisionRequest, setRevisionRequest] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState('comments');
  
  const canApprove = (entityType === 'design' ? canApproveContent : canApproveSchedule) || isManager;
  
  const currentUserId = localStorage.getItem('user_id') || '';
  
  // Get auth headers
  const getHeaders = () => {
    const token = localStorage.getItem('access_token') || localStorage.getItem('token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };
  
  // Fetch comments
  const fetchComments = useCallback(async () => {
    try {
      const response = await axios.get(
        `${API}/approvals/${entityType}s/${entityId}/comments`,
        { headers: getHeaders() }
      );
      setComments(response.data.comments || []);
    } catch (error) {
      console.error('Failed to fetch comments:', error);
    }
  }, [entityType, entityId]);
  
  // Fetch audit log
  const fetchAuditLog = useCallback(async () => {
    try {
      const response = await axios.get(
        `${API}/approvals/audit/${entityType}/${entityId}`,
        { headers: getHeaders() }
      );
      setAuditLog(response.data.audit || []);
    } catch (error) {
      console.error('Failed to fetch audit log:', error);
    }
  }, [entityType, entityId]);
  
  useEffect(() => {
    if (entityId) {
      setLoading(true);
      Promise.all([fetchComments(), fetchAuditLog()]).finally(() => setLoading(false));
    }
  }, [entityId, fetchComments, fetchAuditLog]);
  
  // Submit for approval
  const handleSubmitForApproval = async () => {
    setSubmitting(true);
    try {
      await axios.post(
        `${API}/approvals/${entityType}s/${entityId}/submit`,
        { notes: newComment || null },
        { headers: getHeaders() }
      );
      toast.success(`${entityType === 'design' ? 'Design' : 'Event'} submitted for approval`);
      setNewComment('');
      onStatusChange?.('pending');
      fetchAuditLog();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit for approval');
    } finally {
      setSubmitting(false);
    }
  };
  
  // Approve
  const handleApprove = async () => {
    setSubmitting(true);
    try {
      await axios.post(
        `${API}/approvals/${entityType}s/${entityId}/approve`,
        { notes: newComment || null },
        { headers: getHeaders() }
      );
      toast.success(`${entityType === 'design' ? 'Design' : 'Event'} approved!`);
      setNewComment('');
      onStatusChange?.('approved');
      fetchAuditLog();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve');
    } finally {
      setSubmitting(false);
    }
  };
  
  // Reject
  const handleReject = async () => {
    if (!rejectReason.trim()) {
      toast.error('Please provide a reason for rejection');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(
        `${API}/approvals/${entityType}s/${entityId}/reject`,
        { reason: rejectReason, notes: null },
        { headers: getHeaders() }
      );
      toast.success(`${entityType === 'design' ? 'Design' : 'Event'} rejected`);
      setShowRejectDialog(false);
      setRejectReason('');
      onStatusChange?.('rejected');
      fetchAuditLog();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject');
    } finally {
      setSubmitting(false);
    }
  };
  
  // Request revision
  const handleRequestRevision = async () => {
    if (!revisionRequest.trim()) {
      toast.error('Please provide revision notes');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(
        `${API}/approvals/${entityType}s/${entityId}/request-revision`,
        { notes: revisionRequest },
        { headers: getHeaders() }
      );
      toast.success('Revision requested');
      setShowRevisionDialog(false);
      setRevisionRequest('');
      onStatusChange?.('revision_requested');
      fetchAuditLog();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to request revision');
    } finally {
      setSubmitting(false);
    }
  };
  
  // Cancel submission
  const handleCancelSubmission = async () => {
    setSubmitting(true);
    try {
      await axios.post(
        `${API}/approvals/${entityType}s/${entityId}/cancel`,
        {},
        { headers: getHeaders() }
      );
      toast.success('Submission cancelled');
      onStatusChange?.('not_submitted');
      fetchAuditLog();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel');
    } finally {
      setSubmitting(false);
    }
  };
  
  // Add comment
  const handleAddComment = async () => {
    if (!newComment.trim()) return;
    
    try {
      await axios.post(
        `${API}/approvals/${entityType}s/${entityId}/comments`,
        { content: newComment },
        { headers: getHeaders() }
      );
      setNewComment('');
      fetchComments();
    } catch (error) {
      toast.error('Failed to add comment');
    }
  };
  
  // Delete comment
  const handleDeleteComment = async (commentId) => {
    try {
      await axios.delete(`${API}/approvals/comments/${commentId}`, { headers: getHeaders() });
      fetchComments();
    } catch (error) {
      toast.error('Failed to delete comment');
    }
  };

  return (
    <Card className="dark:bg-slate-800/60" data-testid="approval-panel">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-base flex items-center gap-2 dark:text-white">
              <CheckCircle className="h-4 w-4 text-blue-500" />
              Approval Workflow
            </CardTitle>
            <CardDescription className="dark:text-slate-400">{entityName}</CardDescription>
          </div>
          <ApprovalStatusBadge status={approvalStatus || 'not_submitted'} />
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Status Info */}
        {approvalStatus === 'rejected' && rejectionReason && (
          <div className="p-3 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800">
            <p className="text-sm font-medium text-red-800 dark:text-red-300 mb-1">Rejection Reason:</p>
            <p className="text-sm text-red-700 dark:text-red-400">{rejectionReason}</p>
          </div>
        )}
        
        {approvalStatus === 'revision_requested' && revisionNotes && (
          <div className="p-3 bg-orange-50 dark:bg-orange-900/20 rounded-lg border border-orange-200 dark:border-orange-800">
            <p className="text-sm font-medium text-orange-800 dark:text-orange-300 mb-1">Revision Notes:</p>
            <p className="text-sm text-orange-700 dark:text-orange-400">{revisionNotes}</p>
          </div>
        )}
        
        {/* Action Buttons */}
        {!readOnly && (
          <div className="flex flex-wrap gap-2">
            {(approvalStatus === 'not_submitted' || approvalStatus === 'revision_requested' || approvalStatus === 'rejected') && (
              <Button
                onClick={handleSubmitForApproval}
                disabled={submitting}
                className="bg-blue-600 hover:bg-blue-700"
                data-testid="submit-for-approval-btn"
              >
                {submitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
                {approvalStatus === 'revision_requested' ? 'Resubmit' : 'Submit for Approval'}
              </Button>
            )}
            
            {approvalStatus === 'pending' && canApprove && (
              <>
                <Button
                  onClick={handleApprove}
                  disabled={submitting}
                  className="bg-green-600 hover:bg-green-700"
                  data-testid="approve-btn"
                >
                  {submitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CheckCircle className="h-4 w-4 mr-2" />}
                  Approve
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setShowRevisionDialog(true)}
                  disabled={submitting}
                  className="border-orange-500 text-orange-600 hover:bg-orange-50"
                  data-testid="request-revision-btn"
                >
                  <RotateCcw className="h-4 w-4 mr-2" />
                  Request Revisions
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setShowRejectDialog(true)}
                  disabled={submitting}
                  className="border-red-500 text-red-600 hover:bg-red-50"
                  data-testid="reject-btn"
                >
                  <XCircle className="h-4 w-4 mr-2" />
                  Reject
                </Button>
              </>
            )}
            
            {approvalStatus === 'pending' && !canApprove && (
              <Button
                variant="outline"
                onClick={handleCancelSubmission}
                disabled={submitting}
                data-testid="cancel-submission-btn"
              >
                Cancel Submission
              </Button>
            )}
          </div>
        )}
        
        <Separator />
        
        {/* Tabs: Comments / History */}
        <div className="flex gap-2 border-b dark:border-slate-700">
          <button
            className={`px-3 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
              activeTab === 'comments' 
                ? 'border-blue-500 text-blue-600 dark:text-blue-400' 
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
            onClick={() => setActiveTab('comments')}
          >
            <MessageSquare className="h-4 w-4 inline mr-1" />
            Comments ({comments.length})
          </button>
          <button
            className={`px-3 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
              activeTab === 'history' 
                ? 'border-blue-500 text-blue-600 dark:text-blue-400' 
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
            onClick={() => setActiveTab('history')}
          >
            <History className="h-4 w-4 inline mr-1" />
            History ({auditLog.length})
          </button>
        </div>
        
        {/* Comments Tab */}
        {activeTab === 'comments' && (
          <div className="space-y-3">
            <ScrollArea className="h-48">
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-blue-500" />
                </div>
              ) : comments.length === 0 ? (
                <p className="text-center text-muted-foreground py-8 text-sm">No comments yet</p>
              ) : (
                <div className="space-y-2 pr-4">
                  {comments.map((comment) => (
                    <CommentItem
                      key={comment.id}
                      comment={comment}
                      currentUserId={currentUserId}
                      onDelete={handleDeleteComment}
                    />
                  ))}
                </div>
              )}
            </ScrollArea>
            
            {!readOnly && (
              <div className="flex gap-2">
                <Textarea
                  placeholder="Add a comment..."
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  className="min-h-[60px]"
                  data-testid="comment-input"
                />
                <Button
                  onClick={handleAddComment}
                  disabled={!newComment.trim()}
                  size="sm"
                  data-testid="add-comment-btn"
                >
                  <Send className="h-4 w-4" />
                </Button>
              </div>
            )}
          </div>
        )}
        
        {/* History Tab */}
        {activeTab === 'history' && (
          <ScrollArea className="h-56">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-blue-500" />
              </div>
            ) : auditLog.length === 0 ? (
              <p className="text-center text-muted-foreground py-8 text-sm">No history yet</p>
            ) : (
              <div className="space-y-2 pr-4">
                {auditLog.map((entry, idx) => (
                  <div key={entry.id || idx} className="p-2 bg-slate-50 dark:bg-slate-700/50 rounded text-sm">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <ApprovalStatusBadge status={entry.new_status} size="sm" />
                        <span className="text-muted-foreground">by</span>
                        <span className="font-medium dark:text-white">{entry.performed_by_name || 'Unknown'}</span>
                      </div>
                      <span className="text-xs text-muted-foreground">
                        {entry.performed_at 
                          ? formatDistanceToNow(new Date(entry.performed_at), { addSuffix: true })
                          : ''}
                      </span>
                    </div>
                    {entry.notes && (
                      <p className="text-xs text-muted-foreground mt-1 pl-2 border-l-2 border-slate-300">
                        {entry.notes}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </ScrollArea>
        )}
      </CardContent>
      
      {/* Reject Dialog */}
      <Dialog open={showRejectDialog} onOpenChange={setShowRejectDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject {entityType === 'design' ? 'Design' : 'Event'}</DialogTitle>
            <DialogDescription>
              Please provide a reason for rejection. This will be visible to the creator.
            </DialogDescription>
          </DialogHeader>
          <Textarea
            placeholder="Reason for rejection..."
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            className="min-h-[100px]"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRejectDialog(false)}>Cancel</Button>
            <Button 
              onClick={handleReject} 
              disabled={submitting || !rejectReason.trim()}
              className="bg-red-600 hover:bg-red-700"
            >
              {submitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              Reject
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Revision Dialog */}
      <Dialog open={showRevisionDialog} onOpenChange={setShowRevisionDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Request Revisions</DialogTitle>
            <DialogDescription>
              Describe what changes are needed. The creator will be notified.
            </DialogDescription>
          </DialogHeader>
          <Textarea
            placeholder="What revisions are needed..."
            value={revisionRequest}
            onChange={(e) => setRevisionRequest(e.target.value)}
            className="min-h-[100px]"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRevisionDialog(false)}>Cancel</Button>
            <Button 
              onClick={handleRequestRevision}
              disabled={submitting || !revisionRequest.trim()}
              className="bg-orange-600 hover:bg-orange-700"
            >
              {submitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              Request Revisions
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default ApprovalPanel;
